import { DownloadCloud02, Eye, Trash01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import LogoSmall from 'src/assets/img/logo/logo-icon.svg';
import BadgeCmp from 'src/components/BadgeCmp';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import useOrgBranding from 'src/hooks/useOrgBranding';
import { currentUser } from 'src/redux/auth.slice';
import { trackDocumentDownload } from 'src/services/DocumentsService';
import { downloadPdf, getDateWithSuffixFormat, getFileExtensionTypeImage, getFormattedDate, getShortName, onError } from 'src/utils/CommonFunctions';
import WalToast from 'src/utils/WalToast';

// Copied from reference order.const.ts — maps raw documentType enum to display label
const DOCUMENT_TYPE_LABELS: Record<string, string> = {
    POD: 'POD',
    INVOICE: 'Invoice',
    BOL: 'BOL',
    CARRIER_BOL: 'Carrier BOL',
    CARRIER_INVOICE: 'Carrier invoice',
    RATE_CONFIRMATION: 'Rate confirmation',
    POP: 'POP',
    CUSTOMER_QUOTE: 'Customer quote',
    CUSTOMER_CONFIRMATION: 'Order Confirmation',
    NEW_DOCUMENTS: 'New documents',
    CUSTOMER_INVOICE: 'Customer invoice',
    COMMERCIAL_INVOICE: 'Commercial invoice',
    OTHER: 'Other',
    PARS: 'PARS',
    SHIPMENT: 'Shipment',
    CUSTOMS_DOCUMENT: 'Customs document',
};

interface IProps {
    data: any;
    handleViewDocument?: (document: any) => void;
    handleDeleteDocument?: (documentId: number) => void;
    isReadOnly?: boolean;
    selectedIds?: number[];
    onToggleSelect?: (id: number) => void;
}

const LegDocumentsRow = ({ data, handleViewDocument, handleDeleteDocument, isReadOnly = false, selectedIds, onToggleSelect }: IProps) => {
    const { t } = useTranslation();
    const orgBranding = useOrgBranding();
    const fileNameRef = useRef<HTMLParagraphElement>(null);
    const uploadedByRef = useRef<HTMLParagraphElement>(null);
    const downloadControllerRef = useRef<AbortController | null>(null);
    const cachedVersions = useRef<Record<string, string>>({});
    const user = useSelector(currentUser);
    const [isDownloading, setIsDownloading] = useState(false);
    // WAL order id from the route (/orders/:id or /track/:id) for the portal usage event below.
    const { id: routeOrderId } = useParams();

    const handleDownloadDocument = () => {
        // Large documents take a few seconds — ignore repeat clicks so the in-progress
        // download is never aborted by the user's own second click.
        if (!data?.fileUrl || !data?.file || isDownloading) return;

        // Abort any in-flight download before starting a new one
        downloadControllerRef.current?.abort();
        downloadControllerRef.current = new AbortController();
        const signal = downloadControllerRef.current.signal;

        const currentVersion = data?.updatedAt || data?.lastModified || 'unknown';
        const fileUrl = data?.file && data?.fileUrl ? `${data.fileUrl}${data.file}` : data?.fileUrl;
        const fileName = data?.file || data?.fileName;

        // Cache key per file URL + version — only bypass cache for versions never downloaded
        const cacheKey = `${fileUrl}::${currentVersion}`;
        const isCached = cachedVersions.current[cacheKey];
        const shouldBypassCache = !isCached;

        const downloadOptions = shouldBypassCache ? { isUseCache: false, cacheBuster: currentVersion, signal } : { signal };

        // Portal usage: report the download (fire-and-forget) with the document's type (pod, bol, customer_invoice, ...).
        trackDocumentDownload({ docType: String(data?.documentType || 'other').toLowerCase(), entityType: 'order', entityId: routeOrderId });

        setIsDownloading(true);
        downloadPdf(fileUrl, fileName, downloadOptions).then((isDownloaded) => {
            setIsDownloading(false);

            if (!isDownloaded) {
                WalToast.error(t('Orders.Document_download_failed'));
            }
        });

        if (shouldBypassCache) {
            cachedVersions.current[cacheKey] = 'true';
        }
    };

    const isCarrier = data?.uploadedByType === 'carrier';
    // Carrier uploads resolve through uploadedByCarrier, not uploadedByUser.
    const uploader = isCarrier ? data?.uploadedByCarrier : data?.uploadedByUser;
    // Fall back to the WAL identity (black logo + "Western Alliance") whenever no uploader name
    // resolves — system uploads, but also carrier uploads whose carrier is absent or masked by
    // carrier visibility. Never render a bare "-" here: the customer always sees WAL as the party
    // behind the document, matching the Overview progress card + Carrier & Pricing card.
    const isSystem = data?.uploadedByType === 'system' || !uploader?.name;
    // Only the customer contact who uploaded a document may delete it — never carrier/system uploads.
    const canDelete = !isSystem && !isCarrier && !!user?.id && data?.uploadedByUser?.id === user?.id;
    const orgName = user?.organization?.name || orgBranding.name;
    const uploaderName = isSystem ? orgName : uploader?.name || orgName;
    const docTypeLabel = DOCUMENT_TYPE_LABELS[data?.documentType] || data?.documentType || '-';
    const fileExtension = (data?.file || data?.fileName || '').split('.').pop()?.toLowerCase() || '';
    const fileIcon = getFileExtensionTypeImage(fileExtension);

    const hasFile = data?.fileUrl && data?.file;

    const createdAtRef = useRef<HTMLHeadingElement>(null);

    return (
        <>
            <td className="w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit z-[1] table-cell-with-tooltip !py-0" onClick={(e) => e.stopPropagation()}>
                <CheckBox
                    id={`selectDoc-${data?.id}`}
                    checked={selectedIds?.includes(data?.id) ?? false}
                    onChangeFunc={() => onToggleSelect?.(data?.id)}
                    name={`selectDoc-${data?.id}`}
                    value={String(data?.id)}
                    parentClassName="px-3 h-full items-center"
                />
            </td>
            <td className="min-w-[240px] max-w-[240px]">
                <div className="flex min-w-0 items-center space-x-3">
                    <figure className="flex-none">
                        <img src={fileIcon} alt="file" onError={onError} className="w-5 h-5 min-w-5 flex-none object-contain" />
                    </figure>
                    <TruncateTextCmp message={data?.fileName ?? '-'} refName={fileNameRef} tooltipType="action">
                        <p ref={fileNameRef} className="text-xs leading-[18px] text-grayLight900 font-medium flex-1 truncate">
                            {data?.fileName ?? '-'}
                        </p>
                    </TruncateTextCmp>
                </div>
            </td>
            <td className="min-w-[140px] max-w-[140px]">{data?.legShowId || '-'}</td>
            <td className="min-w-[155px] max-w-[155px]">
                <BadgeCmp style="modern" type="success">
                    {docTypeLabel}
                </BadgeCmp>
            </td>
            <td className="min-w-[160px] max-w-[160px]">
                <TruncateTextCmp message={data?.createdAt ? getDateWithSuffixFormat(getFormattedDate(data.createdAt, 'ddd, MMM Do, YYYY', true, true)) : '-'} refName={createdAtRef}>
                    <h6
                        className="truncate"
                        ref={createdAtRef}
                        dangerouslySetInnerHTML={{
                            __html: data?.createdAt ? getDateWithSuffixFormat(getFormattedDate(data.createdAt, 'ddd, MMM Do, YYYY', true, true)) : '-',
                        }}
                    />
                </TruncateTextCmp>
            </td>
            <td className="min-w-[150px] max-w-[150px]">
                <div className="flex gap-x-2 items-center">
                    <figure className="flex-none">
                        {isSystem ? (
                            // Org identity (appearance table) — uploaded brand logo, black WAL icon as fallback
                            orgBranding.logoSrc ? (
                                <img src={orgBranding.logoSrc} alt={orgName} onError={onError} className="table-profile-icn-circle-xs object-contain" />
                            ) : (
                                <figure className="table-profile-user-circle-xs !bg-utilityBlack flex items-center justify-center p-0.5">
                                    <img src={LogoSmall} alt={orgName} onError={onError} className="w-full h-full" />
                                </figure>
                            )
                        ) : uploader?.image && uploader?.imageUrl ? (
                            <img src={`${uploader.imageUrl}${uploader.image}`} alt="avatar" onError={onError} className="table-profile-icn-circle-xs" />
                        ) : (
                            <div className="table-profile-user-circle-xs">{getShortName(uploaderName)}</div>
                        )}
                    </figure>
                    <TruncateTextCmp message={uploaderName} refName={uploadedByRef} tooltipType="action">
                        <p ref={uploadedByRef} className="text-xs leading-[18px] font-medium flex-1 truncate text-grayLight900">
                            {uploaderName}
                        </p>
                    </TruncateTextCmp>
                </div>
            </td>
            <td className="min-w-[64px] w-[64px] max-w-[64px] sticky right-0 bg-inherit z-[1] table-cell-with-tooltip !py-0">
                <div className="flex items-center gap-1.5 justify-end h-full px-3">
                    {hasFile && (
                        <>
                            {/* While the file is fetching, the icon is swapped for the same daisyUI spinner ButtonCmp
                                renders for its own `loading` prop — the icon-only button has no label for one to sit beside. */}
                            <TooltipCmp message={isDownloading ? t('Orders.Downloading') : t('Common.Download')} childrenClassName="!flex">
                                <ButtonCmp
                                    className={''}
                                    size="none"
                                    icon={isDownloading ? <span className="loading loading-spinner lg:w-4 lg:h-4 h-3 w-3 text-xs"></span> : <DownloadCloud02 className="w-4 h-4 text-grayLight600" />}
                                    disabled={isDownloading}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadDocument();
                                    }}
                                >
                                    <></>
                                </ButtonCmp>
                            </TooltipCmp>
                            <TooltipCmp message="View" childrenClassName="!flex">
                                <ButtonCmp
                                    className={''}
                                    size="none"
                                    icon={<Eye className="w-4 h-4 text-grayLight600" />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewDocument?.(data);
                                    }}
                                >
                                    <></>
                                </ButtonCmp>
                            </TooltipCmp>
                        </>
                    )}
                    {canDelete && !isReadOnly && (
                        <TooltipCmp message="Delete" childrenClassName="!flex">
                            <ButtonCmp
                                className={''}
                                size="none"
                                icon={<Trash01 className="w-4 h-4 text-grayLight600" />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteDocument?.(data?.id);
                                }}
                            >
                                <></>
                            </ButtonCmp>
                        </TooltipCmp>
                    )}
                </div>
            </td>
        </>
    );
};

export default LegDocumentsRow;
